import { useContext, useEffect, useState } from 'react';
import { featureFlagService } from '@/services/featureFlagService';
import { AuthContext } from '@/contexts/authContextDefinition';

export interface FeatureFlagState {
  loading: boolean;
  enabled: boolean;
}

/**
 * Resolve a DB-driven feature flag for the current user.
 *
 * Wraps the async `featureFlagService.isFlagEnabled` (which honors the global
 * `enabled` flag + per-user overrides; it ignores rollout_percentage). Returns
 * `{ loading, enabled }` so callers can show a loader and avoid flashing gated
 * UI before the flag resolves.
 */
export function useFeatureFlag(flagName: string): FeatureFlagState {
  // Read the auth context directly (not the throwing useAuth wrapper) so the
  // hook is safe even when rendered outside an AuthProvider (e.g. unit tests).
  const auth = useContext(AuthContext) as { user?: { id?: string } } | undefined;
  const userId = auth?.user?.id;
  const [state, setState] = useState<FeatureFlagState>({ loading: true, enabled: false });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, enabled: false });
    featureFlagService
      .isFlagEnabled(flagName, userId)
      .then((enabled) => {
        if (!cancelled) {
          setState({ loading: false, enabled });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ loading: false, enabled: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [flagName, userId]);

  return state;
}
